import { useDropzone, DropEvent, FileRejection } from "react-dropzone";
import { cn } from "../utils/cn.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";

export type UploadFileOnDropProps<T extends File> = {
  acceptedFiles: T[];
  fileRejections: FileRejection[];
  event: DropEvent;
};
type UploadFileProps = {
  onDrop: <T extends File>(props: UploadFileOnDropProps<T>) => void;
};

const UploadFileDropZone = ({ onDrop }: UploadFileProps) => {
  const handleOnDrop = <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => {
    onDrop({ acceptedFiles, fileRejections, event });
  };
  const { getRootProps, getInputProps } = useDropzone({ onDrop: handleOnDrop });
  const isGummy = useIsGummy();

  return (
    <div className="flex items-center justify-center w-full">
      <label
        {...getRootProps()}
        htmlFor="dropzone-file"
        className={cn(
          "flex flex-col items-center justify-center w-full h-64 cursor-pointer transition-all",
          isGummy
            ? cn(
                "shadow-sunken",
                "rounded-surface-md bg-white dark:bg-mono-900",
                "shadow-card",
                "hover:bg-mono-50 dark:hover:bg-mono-900/90"
              )
            : "border-2 border-mono-300 border-dashed rounded-lg bg-mono-50 dark:hover:bg-mono-900/90 dark:bg-mono-950 hover:bg-white dark:border-mono-600 dark:hover:border-mono-500"
        )}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <svg
            className="w-8 h-8 mb-4 text-mono-500 dark:text-mono-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 16"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
            />
          </svg>
          <p className="mb-2 text-sm text-mono-500 dark:text-mono-400">
            <span className={isGummy ? "font-extrabold" : "font-semibold"}>
              Click to upload
            </span>{" "}
            or drag and drop
          </p>
          <p
            className={cn(
              "text-xs text-mono-500 dark:text-mono-400",
              isGummy && "font-extrabold"
            )}
          >
            PNG, JPG or GIF
          </p>
        </div>
        <input {...getInputProps()} type="file" className="hidden" />
      </label>
    </div>
  );
};

export { UploadFileDropZone };
